import React, { useCallback, useEffect, useState } from "react";

import { apiFetch } from "~/lib/fetchClient";

/**
 * Top-level admin-only banner consuming /api/v1/team_notifications.
 *
 * The component is gated on admin ability at its mount site in app_router,
 * so this fetch is expected to succeed (or fail only on transient network
 * errors). The `.catch` here covers those transient cases.
 *
 * @see PRD R1 AC 6.2 — first kind shipped is `zapier_disconnected`
 * @returns {JSX.Element|null}
 */
const TeamNotificationsBanner = () => {
  const [notifications, setNotifications] = useState([]);

  useEffect(() => {
    let cancelled = false;
    apiFetch
      .get("/team_notifications")
      .then((data) => {
        if (!cancelled) {
          setNotifications(data.results ?? []);
        }
      })
      .catch(() => {
        // Transient network errors only — leave banners empty on failure.
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const dismiss = useCallback(async (id) => {
    // Capture the dismissed item from within the functional update so we
    // never close over a stale `notifications` snapshot.
    let removed;
    setNotifications((prev) => {
      removed = prev.find((n) => n.id === id);
      return prev.filter((n) => n.id !== id);
    });
    try {
      await apiFetch.post(`/team_notifications/${id}/dismiss`);
    } catch (e) {
      // Restore only the failed item, via a functional update, so a
      // concurrent dismissal of a different banner is not clobbered.
      if (removed) {
        setNotifications((prev) =>
          prev.some((n) => n.id === id) ? prev : [...prev, removed],
        );
      }
    }
  }, []);

  if (notifications.length === 0) {
    return null;
  }

  return (
    <div className="system-banner-stack">
      {notifications.map((n) => (
        <div
          key={n.id}
          className="alert alert-warning alert-dismissible m-0 rounded-0 border-0"
          role="alert"
        >
          <span>
            {n.action_url ? n.message.replace(/\.$/, "") : n.message}
            {n.action_url ? (
              <>
                {" from "}
                <a href={n.action_url} target="_blank" rel="noreferrer">
                  {n.action_text || "here"}
                </a>
                .
              </>
            ) : null}
          </span>
          <button
            type="button"
            className="close"
            aria-label="Dismiss"
            onClick={() => dismiss(n.id)}
          >
            <span aria-hidden="true">&times;</span>
          </button>
        </div>
      ))}
    </div>
  );
};

export default TeamNotificationsBanner;
